/**
 * Standard sampling policies (#2862): one table, `sampling_policies`
 * (gateway-owned, downlink-replicated), serving every sampled stream. Today
 * that is `trace` (message trace bodies) and `lifecycle` (request lifecycle
 * event timelines).
 *
 * A row is (stream × region × scope). Scopes, most specific first:
 * `org_sku` > `org_lane` > `org` > `sku` > `lane` > `region`. A lane is a SKU
 * namespace (`pub_sku_catalog.namespace_slug`, e.g. `claude-code`, `codex`);
 * a request's lane is the namespace of its resolved tenant SKU (#3199).
 * Every value column is nullable and
 * resolved field by field: the first non-null value walking that order wins,
 * so "unset" means "inherit from the broader layer". Nothing set anywhere =
 * keep everything, no size tiering — today's behaviour.
 *
 * Empty table, unreadable table, or a cold gateway cache = today's
 * behaviour for both streams (lifecycle then falls back to the legacy
 * `routing_load_settings.lifecycle_*` columns, byte for byte).
 */

export const SAMPLING_STREAMS = ['trace', 'lifecycle'] as const;
export type SamplingStream = (typeof SAMPLING_STREAMS)[number];

export const SAMPLING_REGIONS = ['jp', 'cn'] as const;
export type SamplingRegion = (typeof SAMPLING_REGIONS)[number];

/** Resolution order, most specific first. */
export const SAMPLING_SCOPES = ['org_sku', 'org_lane', 'org', 'sku', 'lane', 'region'] as const;
export type SamplingScope = (typeof SAMPLING_SCOPES)[number];

/**
 * Where a sampling decision came from, as recorded on
 * `message_traces.sample_rule`. `default` = no row set `keep_ratio`
 * (ratio 1). `forced:*` = a must-keep rule overrode the ratio.
 */
export type TraceSampleRule =
  | SamplingScope
  | 'default'
  | 'forced:error'
  | 'forced:retry'
  | 'forced:billing'
  | 'forced:slow'
  | 'forced:smoke';

export const TRACE_SIZE_TIERS = ['small', 'large_full', 'large_redacted'] as const;
export type TraceSizeTier = (typeof TRACE_SIZE_TIERS)[number];


export interface SamplingPolicyValues {
  /** Share of requests kept, [0, 1]. 0 = only must-keep requests. */
  readonly keep_ratio: number | null;
  /** Requests at least this slow (ms) are always kept. */
  readonly keep_slow_ms: number | null;
  /** trace only: a trace whose captured bodies total at least this many bytes is "large". */
  readonly size_threshold_bytes: number | null;
  /** trace only: share of kept large traces stored with full media, [0, 1]; the rest are media-redacted. */
  readonly large_full_keep_ratio: number | null;
}

export interface SamplingPolicyKey {
  readonly stream: SamplingStream;
  readonly region: SamplingRegion;
  readonly scope: SamplingScope;
  readonly org_id: string | null;
  readonly sku_id: string | null;
  /**
   * SKU namespace (#3199); set exactly for the `lane` and `org_lane` scopes.
   * Optional so a pre-lane key literal still type-checks; absent = null.
   */
  readonly lane?: string | null;
}

/** Lane format the table's CHECK admits: lowercase letters, digits, hyphens. */
export const SAMPLING_LANE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
export const SAMPLING_LANE_MAX_LENGTH = 64;

export interface SamplingPolicyInput extends SamplingPolicyKey, SamplingPolicyValues {
  readonly note?: string | null;
}

export interface SamplingPolicyRecord extends SamplingPolicyInput {
  readonly policy_key: string;
  readonly note: string | null;
  readonly updated_by: string | null;
  readonly updated_at: string;
}

/** The legacy lifecycle knobs on `routing_load_settings`, shown read-only next to the table. */
export interface LegacyLifecycleSamplingValues {
  readonly lifecycle_sample_p_jp: number | null;
  readonly lifecycle_sample_p_cn: number | null;
  readonly lifecycle_keep_org_ids: readonly string[] | null;
  readonly lifecycle_keep_slow_ms: number | null;
}

/**
 * Whether the Tokyo gateway will accept a write to `sampling_policies`
 * (#2862 H2). `sampling_policies` is downlink-replicated; a Tokyo write
 * while CN does not yet hold it as a ready `ndt_downlink` member stalls the
 * whole CN downlink. #3199 adds a second proof: CN's table must also have
 * the `lane` column, otherwise ANY write (the empty-set delete included)
 * stalls it. Single-region databases (no `ndt_downlink` publication) are
 * always open. Closed = `reason` says why and how to check; a PUT is then
 * refused with 403 `cn_downlink_not_ready:`.
 */
export interface SamplingPoliciesWriteGate {
  readonly open: boolean;
  readonly reason: string | null;
  /** `checked_at` of the CN readiness row the decision used; null when none was read. */
  readonly checked_at: string | null;
}

/** Name of the CN `sampling_policies` downlink readiness report (`region:capability`); each report row appends `@<uuid>`. */
export const CN_SAMPLING_POLICIES_DOWNLINK_READINESS_ID = 'cn:sampling_policies_downlink';

/**
 * Name of the CN report proving CN's local `sampling_policies` has the `lane`
 * column (#3199, gateway 0128). Without it a Tokyo write would replicate a
 * tuple with a column CN lacks and stall `ndt_downlink`.
 */
export const CN_SAMPLING_POLICIES_LANE_DOWNLINK_READINESS_ID = 'cn:sampling_policies_lane_downlink';

/** Prefix of the 403 message when the write gate is closed. */
export const CN_DOWNLINK_NOT_READY_PREFIX = 'cn_downlink_not_ready:';

export interface ListSamplingPoliciesResponse {
  readonly policies: readonly SamplingPolicyRecord[];
  /** Optimistic-concurrency token: echo it as `expected_version` on PUT. */
  readonly version: string;
  /**
   * Per stream × region: `table` when at least one row of that stream and
   * region exists, else what the gateway falls back to (`legacy` for
   * lifecycle, `keep_all` for trace).
   */
  readonly sources: Readonly<Record<`${SamplingStream}:${SamplingRegion}`, 'table' | 'legacy' | 'keep_all'>>;
  readonly legacy_lifecycle: LegacyLifecycleSamplingValues | null;
  readonly write_gate: SamplingPoliciesWriteGate;
  /**
   * Distinct `pub_sku_catalog.namespace_slug` values on this gateway, sorted:
   * the lanes a `lane` / `org_lane` row can name (#3199). Empty when the
   * catalog could not be read.
   */
  readonly lane_options: readonly string[];
}

/** Full replace of the policy set. */
export interface PutSamplingPoliciesRequest {
  readonly policies: readonly SamplingPolicyInput[];
  /** `version` from the last GET; a mismatch is 409 and nothing is written. */
  readonly expected_version: string;
  /**
   * Validate, check the version and the write gate, compute warnings — and
   * write nothing. The response has the same shape; `version` and
   * `policies` are the current (unchanged) state. Not audited.
   */
  readonly dry_run?: boolean;
}

export interface PutSamplingPoliciesResponse extends ListSamplingPoliciesResponse {
  /** Non-fatal findings, e.g. an org or SKU id the gateway has never seen. */
  readonly warnings: readonly string[];
  /** Echoes the request: true = nothing was written. */
  readonly dry_run: boolean;
}

export function samplingPolicyKey(key: SamplingPolicyKey): string {
  const base = `${key.stream}:${key.region}:${key.scope}:${key.org_id ?? '*'}:${key.sku_id ?? '*'}`;
  return key.lane === undefined || key.lane === null ? base : `${base}:${key.lane}`;
}
